import { useState, useEffect, useRef } from "react";
import { useMediaQuery } from "../../../lib/hooks";
import {
  IconFileText,
  IconPhoto,
  IconPaletteFilled,
  IconFileDescriptionFilled,
  IconPhotoFilled,
  IconBriefcaseFilled,
  IconBuilding,
  IconFileInvoice,
  IconCurrencyTaka,
  IconReceipt2,
  IconArrowsExchange,
  IconUsers,
  IconSparkles,
  IconBuildingBank,
  IconSettings,
  IconTrendingUp,
  IconTrendingDown,
  IconWallet,
  IconClock,
  IconArrowDownLeft,
  IconArrowUpRight,
  IconCoins,
  IconCalendar,
  IconDots,
  IconDownload,
  IconShare2, IconTrophy,
  IconSpeakerphone,
  IconListDetails,
  IconQrcode,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../../store/authStore";
import { squircleStyle, linkGradient } from "../../../utils/tileStyle";
import ModuleShortcutBadge from "../../../components/common/ModuleShortcutBadge";
import { tileGlyphSize } from '../../../constants/moduleColors';
import { useTranslation } from "../../../i18n/hooks/useTranslation";
import { notifications } from "@/lib/notifications";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  generateDocPdf,
  downloadBlob,
  printBlob,
  shareBlobs,
} from "../../../utils/generateDocPdf";
import PrintModal from "../generate-image/PrintModal";
import CustomDownloadModal from "../generate-image/CustomDownloadModal";
import ImagePreviewModal from "../../../components/common/ImagePreviewModal";
import Carousel from "../../../components/common/EmblaCarousel";
import generatedDocService from "../../../services/generatedDocService";
import imageGenerationService from "../../../services/imageGenerationService";
import officeSpaceService from "../../../services/officeSpaceService";
import { useOfficeSpaceStore } from "../../../store/officeSpaceStore";
import incomeExpenseService from "../../../services/incomeExpenseService";
import usageService from "../../../services/usageService";
import dashboardService from "../../../services/dashboardService";
import { useBuyCreditPath } from "../../../store/paymentConfigStore";
import { creditParts } from "../../../utils/credit";
import { useContestStore, isContestOn } from "../../../store/contestStore";
import LatestJobsSection from "../../../components/user/LatestJobsSection";
import { useIncomeExpenseStore } from "../../../store/incomeExpenseStore";
import { isSpecialBgToken, specialBgCss, hasTransparentSubject } from "../generate-image/bgPresets";
import megamenuData from "../../../../megamenu.json";
import { useToolFavoritesStore } from "../../../store/toolFavoritesStore";
import { ICON_OVERRIDES, isInternalTool, isNewTool } from "../tools/toolCatalog";
import { useHotToolSlugs } from "../tools/hotTools";
import BiggaponModal from "../../../components/common/BiggaponModal";
import RateChartModal from "../../../components/common/RateChartModal";

// ── Sohoz Tools lookup (for showing a user's favorite tools on the dashboard) ──
const TOOLS_SITE_URL = "https://sohozkaj.com";
const toolSlug = (p) => (p || "").replace(/^\/tools\//, "");
// Per-tool folders keep their name on R2 except "general/", which is
// "general-tools/".
const toolIconSrc = (p) =>
  !p
    ? ""
    : `https://files.sohozkaj.com/icons/${p
        .replace(/^\/tools-icon\//, "")
        .replace(/^general\//, "general-tools/")}`;
// slug → tool metadata (name / nameBn / iconImg / path / order / categoryId),
// joined back to favorites. `order` + `categoryId` drive the gradient tint so the
// tiles match the /tools page exactly.
const TOOLS_BY_SLUG = new Map();
megamenuData.categories.forEach((cat) =>
  cat.tools.forEach((tool) =>
    TOOLS_BY_SLUG.set(toolSlug(tool.path), {
      ...tool,
      slug: toolSlug(tool.path),
      categoryId: cat.id,
    }),
  ),
);
// The one in-app tool (opens via router, not the external site).
TOOLS_BY_SLUG.set("form-auto-fillup", {
  name: "Form auto fillup",
  nameBn: "ফর্ম অটো ফিলআপ",
  path: "/tools/form-auto-fillup",
  slug: "form-auto-fillup",
  internal: true,
  categoryId: "result",
  addedAt: "2026-06-28", // first commit of the auto-fillup module
  iconImg: "/tools-icon/form-auto-fill.svg", // → files.sohozkaj.com/icons/form-auto-fill.svg
});
// Some tools (e.g. the result tools) have no CDN icon — render a Tabler vector
// instead, reusing the shared ICON_OVERRIDES map from the /tools grid. Clearing
// iconImg makes the tile fall back to the IconComp branch.
Object.entries(ICON_OVERRIDES).forEach(([slug, IconComp]) => {
  if (TOOLS_BY_SLUG.has(slug)) {
    Object.assign(TOOLS_BY_SLUG.get(slug), { IconComp, iconImg: undefined });
  }
});

// ── Tool tile colours — mirror the /tools page exactly so a favorited tool shows
// the SAME background on both pages. Each category owns a hue region (rainbow
// across categories); tools fan out within a ±BAND window around it. The category
// position (catIdx/catCount) is derived from the same tool ordering as /tools:
// form-auto-fillup first (general), then the megamenu order. ──
const CATEGORY_HUE = { pdf: 265, image: 200, text: 150, video: 35, general: 320, result: 175 };
const FALLBACK_HUE = 220;
const TILE_BAND = 92; // hue spread each side of the category hue

// slug → { categoryId, catIdx, catCount } built in the same order as /tools.
const _tileBase = [
  { slug: "form-auto-fillup", categoryId: "general" },
  ...megamenuData.categories.flatMap((cat) =>
    cat.tools.map((tl) => ({ slug: toolSlug(tl.path), categoryId: cat.id })),
  ),
];
const _tileCatCount = _tileBase.reduce(
  (m, t) => ((m[t.categoryId] = (m[t.categoryId] || 0) + 1), m),
  {},
);
const _tileRunning = {};
const TOOL_TILE = new Map();
_tileBase.forEach((t) => {
  const catIdx = (_tileRunning[t.categoryId] = (_tileRunning[t.categoryId] ?? -1) + 1);
  TOOL_TILE.set(t.slug, { categoryId: t.categoryId, catIdx, catCount: _tileCatCount[t.categoryId] });
});

// CSS gradient for a tool tile (identical formula to /tools).
const tileGradient = (tool) => {
  const meta = TOOL_TILE.get(tool.slug) || { categoryId: tool.categoryId, catIdx: 0, catCount: 1 };
  const base = CATEGORY_HUE[meta.categoryId] ?? FALLBACK_HUE;
  const n = meta.catCount || 1;
  const i = meta.catIdx || 0;
  const frac = n > 1 ? i / (n - 1) : 0.5;
  const hue = ((base - TILE_BAND + 2 * TILE_BAND * frac) % 360 + 360) % 360;
  const sat = 78 - (i % 3) * 6;
  const lTop = 60 - (i % 4) * 3;
  return `linear-gradient(135deg, hsl(${hue.toFixed(0)} ${sat}% ${lTop}%), hsl(${((hue + 16) % 360).toFixed(0)} ${sat}% ${lTop - 12}%))`;
};


// Shown in the "Tools & Features" section when a user hasn't starred any tools
// yet — a curated set of popular Sohoz Tools so the section is never empty.
const DEFAULT_TOOL_SLUGS = [
  "form-auto-fillup",
  "image/compress",
  "image/convert",
  "image/nid-to-pdf",
  "image/passport-to-pdf",
  "pdf/edit",
  "text/image-to-text",
  "image/document-cleanup",
];
const DEFAULT_TOOLS = DEFAULT_TOOL_SLUGS.map((slug) =>
  TOOLS_BY_SLUG.get(slug),
).filter(Boolean);

// ── Quick Links — pinned page shortcuts shown below the tools grid. These are
// in-app navigation links (not Sohoz Tools), rendered with the SAME gradient
// squircle style as the tool tiles. Each owns a fixed hue so the row stays as
// colourful as the tools above it; labels reuse the sidebar's translation keys.
// `bdOnly` items (দরকারি লিংক) are hidden for non-Bangladesh users, matching the
// sidebar gating. ──
// Each link gets its own distinct vibrant hue, ordered so no two neighbours match.
// The set leans blue / warm / pink and deliberately skips the green & teal that
// dominate the tools grid above, so the row still reads as its own block.
const QUICK_LINKS = [
  // Buy credit — owners only (operators spend the owner's credit; the Buy Credit tab is OWNER-only too).
  { id: "buyCredit",     to: "/settings?tab=buycredit",         labelKey: "payments.buyTitle",            fallback: "ক্রেডিট কিনুন",        Icon: IconCoins,        hue: 32,  ownerOnly: true, glossy: true },
  { id: "myFiles",       to: "/documents",                      labelKey: "documents.myGeneratedDocuments", fallback: "আমার ফাইল",         Icon: IconFileText,     hue: 8   },
  { id: "invoice",       to: "/income?tab=invoice",             labelKey: "incomeExpense.invoiceTab",     fallback: "ইনভয়েস",             Icon: IconFileInvoice,  hue: 265 },
  { id: "income",        to: "/income",                         labelKey: "navigation.incomeExpense",     fallback: "আয় ব্যয় হিসেব",       Icon: IconCurrencyTaka, hue: 198 },
  { id: "denapawna",     to: "/income?tab=denapawna",           labelKey: "incomeExpense.denaPawnaTab",    fallback: "দেনা পাওনা",           Icon: IconArrowsExchange, hue: 320 },
  { id: "ieList",        to: "/settings?tab=incomeexpense",     labelKey: "navigation.incomeExpenseList", fallback: "আয় ব্যয় তালিকা",      Icon: IconReceipt2,     hue: 42  },
  { id: "customers",     to: "/income?tab=customers",           labelKey: "incomeExpense.customersTab",   fallback: "গ্রাহক",               Icon: IconUsers,        hue: 330 },
  { id: "photoSettings", to: "/settings?tab=photoeditsettings", labelKey: "profile.defaultPrompts",       fallback: "ফটো এডিট সেটিংস",      Icon: IconSparkles,     hue: 288 },
  // The Photo Booth card (QR poster, receive-files switch…) is the first card of that same Settings tab — its own tile makes it easy to find.
  { id: "photoBooth",    to: "/settings?tab=photoeditsettings", labelKey: "photoCollectorPanel.settingsTitle", fallback: "ফটো বুথ সেটিং",   Icon: IconQrcode,       hue: 212 },
  { id: "dorkari",       to: "/dorkari-links",                  labelKey: "navigation.sorkariLinks",      fallback: "দরকারি লিংক",          Icon: IconBuildingBank, hue: 20,  bdOnly: true, badge: "new" },
  { id: "settings",      to: "/settings",                       labelKey: "settings.title",               fallback: "সেটিংস",               Icon: IconSettings,     hue: 232 },
  { id: "officespaces",  to: "/officespaces",                   labelKey: "navigation.officeSpaces",      fallback: "দোকান ম্যানেজ করুন",    Icon: IconBuilding,     hue: 350 },
  // The two poster editors open their full-screen window right here (`modal`, no page of their own): the job-advertisement poster and the
  // shop's service rate chart. They wear the "New" badge for 30 days from `addedAt`, like the tools do.
  { id: "jobPoster",     modal: "jobPoster",                       labelKey: "dashboard.jobPoster",          fallback: "চাকরির পোস্টার",       Icon: IconSpeakerphone, hue: 160, addedAt: "2026-10-11" },
  { id: "rateChart",     modal: "rateChart",                       labelKey: "dashboard.rateChart",          fallback: "সার্ভিস রেট চার্ট",     Icon: IconListDetails,  hue: 275, addedAt: "2026-10-11" },
  // Contest page — shown only while a contest is on, never for operators (see quickLinks below).
  { id: "contest",       to: "/contest",                        labelKey: "navigation.contest",           fallback: "কনটেস্ট",              Icon: IconTrophy,       hue: 38,  contest: true, badge: "new" },
];

// Recent-photos carousel: size each thumb so ~8 fill the visible width (the rest
// scroll). Width comes from flex-basis; height follows via aspect-ratio, so the
// thumbnails are noticeably bigger than the old fixed 103×138. Dual (landscape)
// photos keep their wider 169:138 ratio at the same row height.
const RECENT_COLS = 8;
const RECENT_GAP = 12; // the carousel's slide gap

// Solid passport-photo icon — a filled person (head + shoulders) inside an
// ID-photo frame. Same call signature as a Tabler icon so it drops into the
// hero-card `Icon` slot.
const IconPassportSolid = ({ size = 24, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    aria-hidden="true"
  >
    <rect
      x="3.75"
      y="2.75"
      width="16.5"
      height="18.5"
      rx="3.2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <circle cx="12" cy="9.2" r="2.9" fill="currentColor" />
    <path
      d="M6.7 18a5.3 5.3 0 0 1 10.6 0 .9.9 0 0 1-.9.9H7.6a.9.9 0 0 1-.9-.9Z"
      fill="currentColor"
    />
  </svg>
);


// Solid magic-wand icon (for the tools card).
const IconWandSolid = ({ size = 24, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M4.55 21.03a1.3 1.3 0 0 1-1.84-1.84L13.4 8.5l1.84 1.84L4.55 21.03Z" />
    <path d="M15.2 6.7l1.5-1.5a1.6 1.6 0 0 1 2.26 0l.34.34a1.6 1.6 0 0 1 0 2.26l-1.5 1.5L15.2 6.7Z" />
    <path d="M18.4 1.6l.63 1.62 1.62.63-1.62.63-.63 1.62-.63-1.62-1.62-.63 1.62-.63.63-1.62Z" />
    <path d="M6.1 2.6l.5 1.28 1.28.5-1.28.5-.5 1.28-.5-1.28-1.28-.5 1.28-.5.5-1.28Z" />
  </svg>
);

const BG_HEX = {
  white: "#ffffff",
  blue: "#2196F3",
  sky_blue: "#03A9F4",
  grey: "#9E9E9E",
  dark_blue: "#3068E1",
  teal: "#00897B",
  off_white: "#FFF8DC",
};

// Style object for a manual-edit card background — handles gradient:/pattern:/preset: tokens too.
// "Recently edited photos": the newest photos from every editor that saves to history (AI photo edit, manual editor,
// Studio Editor, AI Templates), newest first, at most this many.
const RECENT_PHOTOS_MAX = 50;
const photoTime = (h) => new Date(h.createdAt || h.timestamp || 0).getTime() || 0;
function mergeRecentPhotos(lists) {
  const seen = new Set();
  return lists
    .flat()
    .filter((h) => h?.id != null && h.aiImage && !seen.has(h.id) && seen.add(h.id))
    .sort((a, b) => photoTime(b) - photoTime(a))
    .slice(0, RECENT_PHOTOS_MAX);
}

function resolveImageBgStyle(image) {
  // Fires for manual-editor outputs AND AI results with a transparent subject (.png).
  if (!hasTransparentSubject(image) || !image.background) return {};
  const bg = image.background;
  if (bg === "transparent")
    return {
      background:
        "repeating-conic-gradient(#ccc 0% 25%,#fff 0% 50%) 0 0/10px 10px",
    };
  if (isSpecialBgToken(bg)) return specialBgCss(bg) || {};
  return { background: BG_HEX[bg] || bg }; // bg may already be a hex/rgb for custom colors
}

// Local (not UTC) yyyy-MM-dd
function fmtDate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function todayStr() {
  return fmtDate(new Date());
}

// dateFrom/dateTo bounds for the account-summary range tabs (today / this week / this month).
function rangeBounds(range) {
  const now = new Date();
  const to = fmtDate(now);
  if (range === "week") {
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay()); // Sunday → today
    return { dateFrom: fmtDate(start), dateTo: to };
  }
  if (range === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { dateFrom: fmtDate(start), dateTo: to };
  }
  return { dateFrom: to, dateTo: to };
}

export default function Dashboard() {
  // Which tool tiles wear the "hot" badge: the top 3 of each category across all users (same rule as /tools).
  const hotSlugs = useHotToolSlugs();
  const badgeFor = (tool) => (hotSlugs.has(tool.slug) ? "hot" : isNewTool(tool) ? "new" : null);

  const navigate = useNavigate();
  const buyCreditPath = useBuyCreditPath();
  const { user } = useAuthStore();
  // Operators spend the owner's credit, so credit/contest UI is hidden for them.
  const isOperator = user?.role === "OPERATOR";
  // Bangladesh-only features (Document generation, Jobs, Question Papers) are hidden for
  // users whose country is set to a non-Bangladesh country (unset → shown).
  const isBdUser = !user?.country || user.country === "BD";
  const { t, currentLanguage } = useTranslation();
  const isBn = currentLanguage === "bn";
  // Tool favorites — appended after the fixed featured set below.
  const favoriteSlugs = useToolFavoritesStore((s) => s.favorites);
  const loadFavorites = useToolFavoritesStore((s) => s.load);
  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);
  const fmtNum = (n) =>
    isBn
      ? String(n ?? 0).replace(/[0-9]/g, (d) => "০১২৩৪৫৬৭৮৯"[d])
      : (n ?? 0).toLocaleString();
  const fmtAmount = (n) => {
    const raw = Number(n ?? 0).toLocaleString("en-BD", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return isBn ? raw.replace(/[0-9]/g, (d) => "০১২৩৪৫৬৭৮৯"[d]) : raw;
  };
  const fmtRelative = (dateStr) => {
    if (!dateStr) return "";
    const diff = Math.max(0, (Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return t("dashboard.relativeNow");
    if (diff < 3600)
      return t("dashboard.relativeMinutes", {
        n: fmtNum(Math.floor(diff / 60)),
      });
    if (diff < 86400)
      return t("dashboard.relativeHours", {
        n: fmtNum(Math.floor(diff / 3600)),
      });
    const days = Math.floor(diff / 86400);
    if (days === 1) return t("dashboard.relativeYesterday");
    return t("dashboard.relativeDays", { n: fmtNum(days) });
  };
  const dateLabel = new Intl.DateTimeFormat(isBn ? "bn-BD" : "en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());

  // Recent-photos carousel: fewer, bigger thumbs on small screens (partial last
  // one hints there's more to scroll); the fixed 8 only applies on large screens.
  const isRecentMobile = useMediaQuery("(max-width: 640px)");
  const isRecentTablet = useMediaQuery("(min-width: 641px) and (max-width: 1024px)");
  const recentCols = isRecentMobile ? 3.4 : isRecentTablet ? 5.5 : RECENT_COLS;
  const recentTrack = `(100% - ${((recentCols - 1) * RECENT_GAP).toFixed(1)}px) / ${recentCols}`;
  const recentSingleBasis = `calc(${recentTrack})`;
  const recentDualBasis = `calc((${recentTrack}) * ${(169 / 103).toFixed(4)})`;

  // Modal states
  const [showImagePreviewModal, setShowImagePreviewModal] = useState(false);
  const [selectedPreviewImage, setSelectedPreviewImage] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printImageUrl, setPrintImageUrl] = useState("");
  const [printImageId, setPrintImageId] = useState(null);
  const [showCustomDownload, setShowCustomDownload] = useState(false);
  const [posterModal, setPosterModal] = useState(null); // 'jobPoster' | 'rateChart' — the poster editor window that is open
  const [customDownloadImageUrl, setCustomDownloadImageUrl] = useState("");
  const [customDownloadImageId, setCustomDownloadImageId] = useState(null);

  const [stats, setStats] = useState({
    recentTemplateSets: [],
    newlyAddedTemplateSets: [],
    generatedSets: 0,
    generatedDocs: 0,
    generatedImages: 0,
    officeSpacesCount: 0,
    recentImages: [],
    recentGeneratedDocs: [],
    // Aggregate counts for the bottom stat cards (from /api/dashboard/stats).
    aiPhotos: 0,
    manualEdited: 0,
    bulkEdited: 0,
    aiTemplateImages: 0,
    printMedia: 0,
    questionPapers: 0,
    aiDocuments: 0,
  });
  const [incomeSummary, setIncomeSummary] = useState(null);
  const [creditStats, setCreditStats] = useState(null);
  // The contest moved to its own page (/contest); the dashboard only shows a shortcut card to it.
  const contestData = useContestStore((s) => s.data);
  const fetchContest = useContestStore((s) => s.fetchActive);
  useEffect(() => { fetchContest(); }, [fetchContest]);
  const [loading, setLoading] = useState(true);
  const [summaryRange, setSummaryRange] = useState("today");
  const summaryRangeRef = useRef("today");
  const { isOpen: ieModalOpen } = useIncomeExpenseStore();
  const ieWasOpen = useRef(false);
  const { selectedOfficeSpace } = useOfficeSpaceStore();

  // Reload when the active office space changes so office-scoped sections
  // (recent photos, generated docs) only show the selected shop's data.
  useEffect(() => {
    loadDashboardData();
  }, [selectedOfficeSpace?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh income summary (for the active range) when income/expense modal closes
  useEffect(() => {
    if (ieWasOpen.current && !ieModalOpen) {
      incomeExpenseService
        .summary(rangeBounds(summaryRangeRef.current))
        .then((r) => {
          if (r?.data) setIncomeSummary(r.data);
        })
        .catch(() => {});
    }
    ieWasOpen.current = ieModalOpen;
  }, [ieModalOpen]);

  const loadDashboardData = async () => {
    // Scope office-dependent lists to the active shop (falls back to all shops
    // when none is selected, e.g. plain users without office spaces).
    const officeScope = selectedOfficeSpace
      ? { officeSpaceId: selectedOfficeSpace.id }
      : {};
    try {
      const [
        recentlyUsedResponse,
        generatedStatsResponse,
        officeSpacesResponse,
        recentImagesResponse,
        recentGeneratedDocsResponse,
        incomeSummaryResponse,
        creditStatsResponse,
        dashboardStatsResponse,
      ] = await Promise.all([
        generatedDocService.getRecentlyUsedTemplates(6),
        generatedDocService.getStats(),
        officeSpaceService.getUserOfficeSpaces(),
        imageGenerationService.getHistory({ page: 1, limit: RECENT_PHOTOS_MAX, ...officeScope }),
        generatedDocService.getGeneratedDocs({ page: 1, limit: 6, ...officeScope }),
        incomeExpenseService
          .summary(rangeBounds(summaryRangeRef.current))
          .catch(() => ({ data: null })),
        usageService.getCreditStats().catch(() => null),
        dashboardService.getStats(selectedOfficeSpace?.id ?? null).catch(() => null),
      ]);
      // The other editors' photos (their own tables), each failing quietly — merged with the AI photos below.
      const otherRecent = await Promise.all(
        ["manual", "studio", "ai-templates"].map((source) =>
          imageGenerationService
            .getHistory({ page: 1, limit: RECENT_PHOTOS_MAX, source, ...officeScope })
            .then((r) => r.data?.history || [])
            .catch(() => []),
        ),
      );

      setStats({
        recentTemplateSets: recentlyUsedResponse.data || [],
        generatedSets: generatedStatsResponse.data?.totalGroups || 0,
        // The /stats endpoint counts docs across ALL office spaces, so scope the
        // card to the active shop via the (already office-scoped) docs list total.
        generatedDocs:
          recentGeneratedDocsResponse.pagination?.total
          ?? generatedStatsResponse.data?.totalDocs
          ?? 0,
        generatedImages: recentImagesResponse.data?.pagination?.total || 0,
        officeSpacesCount: officeSpacesResponse.data?.length || 0,
        recentImages: mergeRecentPhotos([recentImagesResponse.data?.history || [], ...otherRecent]),
        recentGeneratedDocs: recentGeneratedDocsResponse.data || [],
        // Aggregate counts (office-scoped) for the bottom stat cards.
        aiPhotos: dashboardStatsResponse?.data?.aiPhotos || 0,
        manualEdited: dashboardStatsResponse?.data?.manualEdited || 0,
        bulkEdited: dashboardStatsResponse?.data?.bulkEdited || 0,
        aiTemplateImages: dashboardStatsResponse?.data?.aiTemplateImages || 0,
        generatedDocsCount: dashboardStatsResponse?.data?.documents || 0,
        printMedia: dashboardStatsResponse?.data?.printMedia || 0,
        questionPapers: dashboardStatsResponse?.data?.questionPapers || 0,
        aiDocuments: dashboardStatsResponse?.data?.aiDocuments || 0,
      });
      setIncomeSummary(incomeSummaryResponse.data);
      setCreditStats(creditStatsResponse);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const changeRange = async (range) => {
    if (range === summaryRange) return;
    setSummaryRange(range);
    summaryRangeRef.current = range;
    try {
      const r = await incomeExpenseService.summary(rangeBounds(range));
      if (r?.data) setIncomeSummary(r.data);
    } catch {
      /* keep previous summary on error */
    }
  };



  const getLanguageBadgeColor = (language) => {
    switch (language) {
      case "bangla":
        return "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
      case "english":
        return "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
      case "mixed":
        return "bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400";
      default:
        return "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400";
    }
  };

  const handleHistoryImageClick = (image) => {
    setSelectedPreviewImage(image);
    setShowImagePreviewModal(true);
  };

  const handleImageDeleted = () => {
    loadDashboardData();
  };

  // ── Document actions (download / print / share) — same flow as /documents ──
  const handleDownloadDoc = async (group) => {
    const notifId = "dash-pdf-download";
    const doc = group?.generatedDocs?.[0];
    const templateDoc = (group?.templateSet?.docs || [])[0];
    if (!templateDoc?.contentKey) {
      notifications.show({
        title: t("common.error"),
        message: t("documents.fileNotAvailable"),
        color: "red",
      });
      return;
    }
    notifications.show({
      id: notifId,
      title: t("documents.downloadingPdf"),
      message: t("documents.downloadingMessage"),
      color: "blue",
      loading: true,
      autoClose: false,
    });
    try {
      const blob = await generateDocPdf(
        templateDoc,
        group?.templateSet?.placeholders || [],
        group?.placeholderValues || {},
      );
      downloadBlob(blob, `${doc?.title || group?.title || "document"}.pdf`);
      notifications.update({
        id: notifId,
        title: t("common.success"),
        message: t("documents.pdfDownloaded"),
        color: "green",
        loading: false,
        autoClose: 3000,
      });
    } catch (error) {
      notifications.update({
        id: notifId,
        title: t("common.error"),
        message: error.message,
        color: "red",
        loading: false,
        autoClose: 3000,
      });
    }
  };

  const handlePrintDocGroup = async (group) => {
    const notifId = "dash-pdf-print";
    const templateDoc = (group?.templateSet?.docs || [])[0];
    if (!templateDoc?.contentKey) {
      notifications.show({
        title: t("common.error"),
        message: t("documents.fileNotAvailableForPrint"),
        color: "red",
      });
      return;
    }
    notifications.show({
      id: notifId,
      title: t("documents.preparingPdf"),
      message: t("documents.convertingForPrint"),
      color: "blue",
      loading: true,
      autoClose: false,
    });
    try {
      const blob = await generateDocPdf(
        templateDoc,
        group?.templateSet?.placeholders || [],
        group?.placeholderValues || {},
      );
      const opened = printBlob(blob);
      if (!opened) {
        notifications.update({
          id: notifId,
          title: t("common.error"),
          message: t("documents.allowPopups"),
          color: "red",
          loading: false,
          autoClose: 3000,
        });
        return;
      }
      notifications.update({
        id: notifId,
        title: t("common.success"),
        message: t("documents.pdfOpenedForPrint"),
        color: "green",
        loading: false,
        autoClose: 2000,
      });
    } catch (error) {
      notifications.update({
        id: notifId,
        title: t("common.error"),
        message: error.message,
        color: "red",
        loading: false,
        autoClose: 3000,
      });
    }
  };

  const handleShareDoc = async (group) => {
    const notifId = "dash-doc-share";
    const templateDocs = group?.templateSet?.docs || [];
    const placeholders = group?.templateSet?.placeholders || [];
    const fieldValues = group?.placeholderValues || {};
    if (!templateDocs.length) {
      notifications.show({
        title: t("common.error"),
        message: t("documents.fileNotAvailable"),
        color: "red",
      });
      return;
    }
    notifications.show({
      id: notifId,
      title: t("documents.preparingShare") || "শেয়ারের জন্য তৈরি হচ্ছে...",
      color: "blue",
      loading: true,
      autoClose: false,
    });
    try {
      const fileResults = await Promise.all(
        (group.generatedDocs || []).map(async (genDoc, i) => {
          const templateDoc = templateDocs[i] ?? templateDocs[0];
          if (!templateDoc?.contentKey) return null;
          const blob = await generateDocPdf(
            templateDoc,
            placeholders,
            fieldValues,
          );
          return new File(
            [blob],
            `${genDoc.title || group.title || "document"}.pdf`,
            { type: "application/pdf" },
          );
        }),
      );
      const files = fileResults.filter(Boolean);
      notifications.hide(notifId);
      if (!files.length) {
        notifications.show({
          title: t("common.error"),
          message: t("documents.fileNotAvailable"),
          color: "red",
        });
        return;
      }
      const used = await shareBlobs(files, group.title || "");
      if (!used)
        notifications.show({
          message: t("documents.pdfDownloading") || "PDF ডাউনলোড হচ্ছে।",
          color: "blue",
          autoClose: 2000,
        });
    } catch (err) {
      notifications.hide(notifId);
      if (err.name !== "AbortError")
        notifications.show({
          title: t("common.error"),
          message: err.message,
          color: "red",
        });
    }
  };

  // Credit usage figures (remaining / used / total).
  // "used" = all-time credits spent (from /api/credits/stats); total = remaining + used.
  const creditRemaining = user?.credit ?? 0;
  const creditUsed = creditStats?.allTime ?? 0;
  const creditTotal = creditRemaining + creditUsed;
  // Big "remaining" figure mirrors the profile-dropdown split, but LABELLED so users
  // aren't confused by two numbers: "পুরনো ক্রেডিট X • ক্রেডিট Y" when the owner holds
  // both, "পুরনো ক্রেডিট X" for legacy-only, else the plain wallet total. (Combined
  // `creditRemaining` still drives the bar/total.)
  const {
    showBoth: showBothCredit,
    showLegacyOnly: showLegacyOnlyCredit,
    legacy: legacyCredit,
    wallet: walletCredit,
  } = creditParts(user);
  const creditShowLabels = showBothCredit || showLegacyOnlyCredit;
  const creditRemainingText = showBothCredit
    ? `${t("profile.creditOldLabel")} ${fmtNum(legacyCredit)} • ${t("profile.creditNewLabel")} ${fmtNum(walletCredit)}`
    : showLegacyOnlyCredit
      ? `${t("profile.creditNewLabel")} ${fmtNum(legacyCredit)}`
      : fmtNum(walletCredit);
  // Bar shows REMAINING credits so it visually depletes (shrinks) as credits
  // are spent, rather than filling up.
  const creditPct =
    creditTotal > 0
      ? Math.min(100, Math.round((creditRemaining / creditTotal) * 100))
      : 0;

  const rangeLabel = t(
    `dashboard.range${summaryRange === "week" ? "Week" : summaryRange === "month" ? "Month" : "Today"}`,
  );

  // Account-summary tiles
  const summaryTiles = [
    {
      key: "income",
      label: t("dashboard.incomeLabel"),
      value: incomeSummary?.totalIncome,
      caption: rangeLabel,
      Icon: IconTrendingUp,
      tile: "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/60",
      accent: "text-emerald-600 dark:text-emerald-400",
    },
    {
      key: "expense",
      label: t("dashboard.expenseLabel"),
      value: incomeSummary?.totalExpense,
      caption:
        (incomeSummary?.totalExpense ?? 0) > 0
          ? rangeLabel
          : t("dashboard.stable"),
      Icon: IconTrendingDown,
      tile: "bg-rose-50 dark:bg-rose-900/20 border-rose-100 dark:border-rose-800/60",
      accent: "text-rose-500 dark:text-rose-400",
    },
    {
      key: "balance",
      label: t("dashboard.balanceLabel"),
      value: incomeSummary?.balance,
      caption: t("dashboard.currentBalance"),
      Icon: IconWallet,
      tile: "bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/60",
      accent: "text-blue-600 dark:text-blue-400",
    },
  ];

  // Featured module cards (top row) — squircle tile with a per-module gradient
  // background and a centered icon (from /public/module-icons), title below.
  // `bg` is a full literal Tailwind arbitrary gradient so the JIT keeps it.
  const heroCards = [
    {
      key: "aiPhoto",
      icon: "/module-icons/ai-tools.svg",
      bg: "bg-[linear-gradient(145deg,#1b1932,#2f2e51)]",
      onClick: () => navigate("/generate-image"),
    },
    {
      key: "studio",
      icon: "/module-icons/studio-editor-tile.svg", // dark camera on the teal tile (the menus use the teal studio-editor.svg)
      bg: "bg-[linear-gradient(145deg,#22E5C5,#14b8a6)]",
      onClick: () => navigate("/studio-editor"),
    },
    // Manual Photo, Bulk Photo Edit, Document and Jobs are not on the home row any more — they are reached from the menu / search
    // (and, where they have one, their Alt + number).
    {
      key: "aiDocuments",
      icon: "/module-icons/ai-documents.svg",
      bg: "bg-[linear-gradient(145deg,#E68A00,#FBA53A)]",
      onClick: () => navigate("/ai-documents"),
    },
    {
      key: "aiTemplate",
      icon: "/module-icons/ai-templates.svg",
      bg: "bg-[linear-gradient(145deg,#a78bfa,#6d28d9)]",
      onClick: () => navigate("/ai-templates"),
    },
    {
      key: "print",
      icon: "/module-icons/print-media.svg",
      bg: "bg-[linear-gradient(145deg,#fb923c,#ea580c)]",
      onClick: () => navigate("/print-media"),
    },
    {
      key: "sohozTools",
      icon: "/module-icons/easy-tools.svg",
      bg: "bg-[linear-gradient(145deg,#ef465f,#f7949b)]",
      onClick: () => navigate("/tools"),
    },
  ];

  // A user's favorite Sohoz Tools — shown in the "Tools & Features" section.
  const openTool = (tool) =>
    isInternalTool(tool)
      ? navigate(tool.path)
      : window.open(
          TOOLS_SITE_URL + tool.path,
          "_blank",
          "noopener,noreferrer",
        );
  // The fixed featured set is always shown first (so the row never empties out),
  // then the user's own favorites are appended — skipping any already featured.
  const favoriteExtras = [...favoriteSlugs]
    .filter((slug) => !DEFAULT_TOOL_SLUGS.includes(slug))
    .map((slug) => TOOLS_BY_SLUG.get(slug))
    .filter(Boolean);
  const showcaseTools = [...DEFAULT_TOOLS, ...favoriteExtras];

  // Pinned page shortcuts (below the tools grid). Hide দরকারি লিংক for non-BD users.
  // The Contest shortcut shows for owners while a contest is on (live, in its hold, or about to start).
  const quickLinks = QUICK_LINKS.filter((l) => (isBdUser || !l.bdOnly) && (!l.contest || (!isOperator && isContestOn(contestData))) && (!l.ownerOnly || user?.role === "OWNER"));
  // Buy Credit follows the same region-aware path as the credit card's button (useBuyCreditPath).
  const openQuickLink = (item) => (item.modal ? setPosterModal(item.modal) : navigate(item.id === "buyCredit" ? buyCreditPath : item.to));

  // Bottom stat cards — `grad` tints the gradient icon chip, `tint` the card bg.
  // Each card + its icon chip share the same Coolors palette hue.
  // Icons reuse the exact module SVGs from the header/sidebar nav (public/module-icons),
  // rendered as white glyphs (brightness-0 invert) on the coloured gradient chips.
  const bottomStats = [
    {
      value: stats.aiPhotos,
      label: t("dashboard.statAiPhotos"),
      iconImg: "/module-icons/ai-tools.svg",
      grad: "from-[#4361ee] to-[#3a0ca3] shadow-[#4361ee]/30",
    },
    {
      value: stats.manualEdited,
      label: t("dashboard.statManualEdited"),
      iconImg: "/module-icons/manual-photo-edit.svg",
      grad: "from-[#4cc9f0] to-[#4361ee] shadow-[#4cc9f0]/30",
    },
    {
      value: stats.bulkEdited,
      label: t("dashboard.statBulkEdited"),
      iconImg: "/module-icons/bulk-photo-editing.svg",
      grad: "from-[#f72585] to-[#7209b7] shadow-[#f72585]/30",
    },
    {
      value: stats.aiTemplateImages,
      label: t("dashboard.statAiTemplateImages"),
      iconImg: "/module-icons/ai-templates.svg",
      grad: "from-[#7209b7] to-[#3a0ca3] shadow-[#7209b7]/30",
    },
    {
      value: stats.generatedDocsCount ?? stats.generatedDocs,
      label: t("dashboard.statDocuments"),
      iconImg: "/module-icons/document-set.svg",
      grad: "from-[#3a86ff] to-[#023e8a] shadow-[#3a86ff]/30",
      bdOnly: true,
    },
    {
      value: stats.printMedia,
      label: t("dashboard.statPrintMedia"),
      iconImg: "/module-icons/print-media.svg",
      grad: "from-[#2a9d8f] to-[#1d3557] shadow-[#2a9d8f]/30",
    },
    {
      value: stats.questionPapers,
      label: t("dashboard.statQuestionPapers"),
      iconImg: "/questions-create.svg",
      grad: "from-[#f77f00] to-[#d62828] shadow-[#f77f00]/30",
      bdOnly: true,
    },
    {
      value: stats.aiDocuments,
      label: t("dashboard.statAiDocuments"),
      iconImg: "/module-icons/ai-documents.svg",
      grad: "from-[#06d6a0] to-[#118ab2] shadow-[#06d6a0]/30",
    },
  ].filter((c) => isBdUser || !c.bdOnly);

  return (
    <div className="w-full font-hind space-y-5 sm:space-y-6 py-2 sm:py-4">
      {/* ── Header ── */}
 

      {/* ── Featured gradient hero cards ── */}
      {/* Symmetric pt/pb so the divider below sits centered between the two
          sections (the parent `space-y` otherwise collapses the hr's top margin). */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-x-2 gap-y-0 sm:gap-4 py-8 sm:py-14 lg:py-10 xl:py-14">
        {heroCards.map(({ key, icon, bg, onClick }) => (
          <button
            key={key}
            onClick={onClick}
            className="group flex flex-col items-center gap-1 sm:gap-3 text-center rounded-2xl sm:rounded-3xl px-1 py-1 sm:px-2 sm:py-5 transition-all duration-200 hover:bg-white dark:hover:bg-zinc-800"
          >
            {/* relative wrapper: the Alt + number hint badge sits in the icon's top-right corner (the squircle's mask would clip it) */}
            <div className="relative w-full max-w-[88px] sm:max-w-[160px] lg:max-w-[138px] xl:max-w-[165px]">
              <div
                style={squircleStyle}
                className={`flex aspect-square w-full items-center justify-center drop-shadow-lg ${bg}`}
              >
                <img
                  src={icon}
                  alt=""
                  className={`${tileGlyphSize(icon)} object-contain`}
                  draggable="false"
                />
              </div>
              <ModuleShortcutBadge id={key} />
            </div>
            <div className="text-[11px] sm:text-[15px] font-extrabold leading-tight tracking-tight text-zinc-900 dark:text-zinc-100">
              {t(`dashboard.toolGrid.${key}.title`)}
            </div>
          </button>
        ))}
      </div>
 

      {/* Divider between hero cards and pinned tools */}
      <hr className="!mt-0 border-zinc-200 dark:border-zinc-800" />

      {/* ── Tools & Features — a fixed set of featured Sohoz Tools. ── */}
      {/* Padding-only top spacing (kill the parent space-y margin). */}
      <div className="!mt-0 pt-8 sm:pt-14">
        <div className="grid grid-cols-5 gap-x-0.5 gap-y-1 sm:grid-cols-4 sm:gap-x-1 sm:gap-y-4 lg:grid-cols-8">
            {showcaseTools.map((tool) => {
              const title = isBn ? tool.nameBn || tool.name : tool.name;
              const badge = badgeFor(tool);
              return (
                <div
                  key={tool.slug}
                  onClick={() => openTool(tool)}
                  className="group flex cursor-pointer flex-col items-center rounded-xl sm:rounded-3xl px-0.5 py-1.5 sm:px-2 sm:py-2.5 text-center transition-colors hover:bg-white dark:hover:bg-zinc-800"
                >
                  <div className="relative">
                    {/* Gradient icon tile */}
                    <div
                      style={{ ...squircleStyle, backgroundImage: tileGradient(tool) }}
                      className="flex aspect-square w-[60px] items-center justify-center drop-shadow-lg transition-transform duration-150 group-hover:scale-[1.03] sm:w-[84px] md:w-[100px] lg:w-[104px] xl:w-[120px]"
                    >
                      {tool.iconImg ? (
                        <img
                          src={toolIconSrc(tool.iconImg)}
                          alt={title}
                          className="h-[34px] w-[34px] object-contain brightness-0 invert sm:h-[50px] sm:w-[50px] md:h-[60px] md:w-[60px] xl:h-[72px] xl:w-[72px]"
                          loading="lazy"
                        />
                      ) : tool.IconComp ? (
                        <tool.IconComp className="text-white w-1/2 h-1/2" />
                      ) : (
                        <IconBriefcaseFilled className="text-white w-1/2 h-1/2" />
                      )}
                    </div>

                    {/* Hot / New badge */}
                    {badge && (
                      <span
                        className={`absolute -left-0.5 -top-0.5 sm:-left-1 sm:-top-1 rounded-full px-1 py-0 text-[7px] sm:px-2.5 sm:py-[3px] sm:text-[10px] font-bold text-white shadow-md ${
                          badge === "hot" ? "bg-red-500" : "bg-emerald-600"
                        }`}
                      >
                        {t(`toolsPage.badge.${badge}`)}
                      </span>
                    )}
                  </div>

                  <div className="mt-1.5 sm:mt-4 max-w-[58px] sm:max-w-[170px] text-[8.5px] sm:text-[12px] md:text-[13px] lg:text-[14px] font-semibold leading-tight text-zinc-800 dark:text-zinc-100">
                    {title}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Divider between pinned tools and quick links */}
      <hr className="!mt-8 sm:!mt-14 border-zinc-200 dark:border-zinc-800" />

      {/* ── Quick Links — pinned page shortcuts (same tile style as the tools grid). ── */}
      <div className="!mt-0 pt-8 sm:pt-14">
        <div className="grid grid-cols-5 gap-x-0.5 gap-y-1 sm:grid-cols-4 sm:gap-x-1 sm:gap-y-4 lg:grid-cols-8">
          {quickLinks.map((item) => {
            const Icon = item.Icon;
            return (
              <div
                key={item.id}
                onClick={() => openQuickLink(item)}
                className="group flex cursor-pointer flex-col items-center rounded-xl sm:rounded-3xl px-0.5 py-1.5 sm:px-2 sm:py-2.5 text-center transition-colors hover:bg-white dark:hover:bg-zinc-800"
              >
                <div className={`relative ${item.glossy ? "drop-shadow-[0_6px_14px_rgba(230,138,0,0.55)]" : ""}`}>
                  {/* Gradient icon tile */}
                  <div
                    style={{ ...squircleStyle, backgroundImage: linkGradient(item.hue) }}
                    className="relative flex aspect-square w-[60px] items-center justify-center drop-shadow-lg transition-transform duration-150 group-hover:scale-[1.03] sm:w-[84px] md:w-[100px] lg:w-[104px] xl:w-[120px]"
                  >
                    <Icon className="relative text-white w-1/2 h-1/2" stroke={1.9} />
                    {/* Glossy finish (the squircle mask clips it): a soft light cap on the top half + a shine that sweeps across now and then. */}
                    {item.glossy && (
                      <>
                        <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/50 to-white/0" />
                        <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
                          <span className="tile-shine absolute inset-y-0 -left-1/2 w-1/3 bg-gradient-to-r from-white/0 via-white/60 to-white/0" />
                        </span>
                      </>
                    )}
                  </div>

                  {/* New badge (matches the sidebar) */}
                  {(item.badge || (isNewTool(item) ? "new" : null)) && (
                    <span className="absolute -left-0.5 -top-0.5 sm:-left-1 sm:-top-1 rounded-full px-1 py-0 text-[7px] sm:px-2.5 sm:py-[3px] sm:text-[10px] font-bold text-white shadow-md bg-emerald-600">
                      {t(`toolsPage.badge.${item.badge || "new"}`)}
                    </span>
                  )}
                </div>

                <div className="mt-1.5 sm:mt-4 max-w-[58px] sm:max-w-[170px] text-[8.5px] sm:text-[12px] md:text-[13px] lg:text-[14px] font-semibold leading-tight text-zinc-800 dark:text-zinc-100">
                  {t(item.labelKey) || item.fallback}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Recently edited photos ── */}
      {/* Extra top gap above this section (important beats the parent space-y). */}
      {(loading || stats.recentImages.length > 0) && (
        <div className="!mt-8 sm:!mt-12 bg-[linear-gradient(135deg,#4361ee14,#7209b714,#f7258514)] dark:bg-none dark:bg-zinc-800/60 border border-[#4361ee]/20 dark:border-zinc-700 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
              {t("dashboard.recentEditedPhotos")}
            </span>
            <button
              onClick={() => navigate("/documents?tab=images")}
              className="text-[13px] font-semibold text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors"
            >
              {t("common.viewAll")} →
            </button>
          </div>
          {/* The editors' smooth Embla drag strip (as in the Studio Editor); a drag never counts as a click on a photo. */}
          <Carousel dragFree slideGap={RECENT_GAP} align="start" loop={false} styles={{ viewport: { paddingBottom: 4 } }}>
            {/* While the history request is in flight the real array is empty; render
                skeleton cards at the real card dimensions so the section reserves its
                height instead of popping in from 0 once data arrives. */}
            {loading && stats.recentImages.length === 0
              ? Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={`recent-skeleton-${i}`}
                    className="flex-shrink-0 rounded-xl border border-zinc-200 dark:border-zinc-700"
                    style={{
                      flexBasis: recentSingleBasis,
                      aspectRatio: "103 / 138",
                      background:
                        "linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%)",
                      backgroundSize: "200% 100%",
                      animation:
                        "dashboard-skeleton-shimmer 1.4s infinite linear",
                    }}
                  />
                ))
              : stats.recentImages.map((image) => {
              const isDual = image.photoSize === "dual_image";
              const manualBg = resolveImageBgStyle(image);
              return (
                <div
                  key={image.id}
                  className="flex-shrink-0 relative rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-700"
                  style={{
                    flexBasis: isDual ? recentDualBasis : recentSingleBasis,
                    aspectRatio: isDual ? "169 / 138" : "103 / 138",
                    cursor: "pointer",
                    ...manualBg,
                  }}
                  onClick={() => handleHistoryImageClick(image)}
                >
                  <div
                    className="absolute inset-0 z-10 transition-opacity duration-300"
                    style={{
                      background:
                        "linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%)",
                      backgroundSize: "200% 100%",
                      animation:
                        "dashboard-skeleton-shimmer 1.4s infinite linear",
                    }}
                  />
                  <img
                    src={image.aiImage || image.originalImage}
                    alt="Generated"
                    draggable="false"
                    className="w-full h-full"
                    style={{
                      objectFit: isDual ? "contain" : "cover",
                      background: isDual ? "#111" : undefined,
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                      position: "relative",
                      zIndex: 2,
                    }}
                    onLoad={(e) => {
                      e.target.style.opacity = "1";
                      const skeleton = e.target.previousSibling;
                      if (skeleton) skeleton.style.opacity = "0";
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                      const skeleton = e.target.previousSibling;
                      if (skeleton) skeleton.style.display = "none";
                    }}
                  />
                </div>
              );
                })}
          </Carousel>
        </div>
      )}

      {/* ── Latest jobs (3 newest from /jobs) — Bangladesh-only feature. Hidden for users
          whose country is set to a non-Bangladesh country (unset → shown). ── */}
      {(!user?.country || user.country === "BD") && <LatestJobsSection />}

      {/* ── Finance summary + Leaderboard ── */}
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-[18px] items-start bg-blue-100/60 dark:bg-blue-900/15 border border-blue-200/60 dark:border-blue-900/25 rounded-2xl p-4 sm:p-5">
        {/* Left column: account summary + stat cards */}
        <div className="w-full flex flex-col gap-4">
          {/* Account summary */}
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-2xl p-5 shadow-sm w-full">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">
                  {t("dashboard.accountSummary")}
                </span>
                <span className="text-[11px] text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700 px-2 py-0.5 rounded-full">
                  {t("dashboard.live")}
                </span>
              </div>
              <div className="inline-flex gap-0.5 bg-zinc-100 dark:bg-zinc-800 rounded-lg p-0.5">
                {["today", "week", "month"].map((r) => (
                  <button
                    key={r}
                    onClick={() => changeRange(r)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-colors ${
                      summaryRange === r
                        ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-sm"
                        : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                    }`}
                  >
                    {t(
                      `dashboard.range${r === "week" ? "Week" : r === "month" ? "Month" : "Today"}`,
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              {summaryTiles.map(
                ({ key, label, value, caption, Icon, tile, accent }) => (
                  <button
                    key={key}
                    onClick={() => navigate("/income")}
                    className={`text-left p-3.5 border rounded-xl transition-shadow hover:shadow-sm ${tile}`}
                  >
                    <div className="flex items-center justify-between mb-2.5">
                      <span className={`text-[13px] font-medium ${accent}`}>
                        {label}
                      </span>
                      <span
                        className={`w-7 h-7 rounded-lg bg-white dark:bg-zinc-900 flex items-center justify-center ${accent}`}
                      >
                        <Icon size={15} stroke={1.8} />
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
                      ৳&thinsp;{fmtAmount(value)}
                    </div>
                    <div className={`text-xs font-semibold mt-1.5 ${accent}`}>
                      {caption}
                    </div>
                  </button>
                ),
              )}

              {/* বাকি — single card holding both দিলাম (receivable) & নিলাম (payable) */}
              <button
                onClick={() => navigate("/income")}
                className="text-left p-3.5 border rounded-xl transition-shadow hover:shadow-sm bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/60"
              >
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[13px] font-medium text-amber-600 dark:text-amber-400">
                    {t("dashboard.dueLabel")}
                  </span>
                  <span className="w-7 h-7 rounded-lg bg-white dark:bg-zinc-900 flex items-center justify-center text-amber-600 dark:text-amber-400">
                    <IconClock size={15} stroke={1.8} />
                  </span>
                </div>
                <div className="space-y-1.5">
                  {/* দিলাম — money owed to the user */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1 text-[11px] font-medium text-amber-600 dark:text-amber-400 whitespace-nowrap">
                      <IconArrowDownLeft size={13} stroke={2} />{" "}
                      {t("dashboard.dueGivenLabel")}
                    </span>
                    <span className="text-base font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
                      ৳&thinsp;{fmtAmount(incomeSummary?.totalDueIncome)}
                    </span>
                  </div>
                  {/* নিলাম — money the user owes */}
                  <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-amber-100 dark:border-amber-800/50">
                    <span className="flex items-center gap-1 text-[11px] font-medium text-orange-600 dark:text-orange-400 whitespace-nowrap">
                      <IconArrowUpRight size={13} stroke={2} />{" "}
                      {t("dashboard.dueTakenLabel")}
                    </span>
                    <span className="text-base font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
                      ৳&thinsp;{fmtAmount(incomeSummary?.totalDueExpense)}
                    </span>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Credit bar — sits directly under the account summary.
              Hidden for operators (their usage draws down the owner's credit). */}
          {!isOperator && (
          <div className="relative overflow-hidden bg-gradient-to-br from-primary-50/70 to-white dark:from-zinc-800/60 dark:to-zinc-900 border border-primary-100 dark:border-zinc-700 rounded-2xl p-5 shadow-sm">
            {/* Top row: balance + recharge */}
            <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
              <div className="flex items-center gap-3.5">
                <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-orange-400 text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-primary/30">
                  <IconCoins size={22} stroke={1.7} />
                </span>
                <div>
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    <span className={`${creditShowLabels ? "text-base" : "text-2xl"} leading-none font-bold text-zinc-900 dark:text-zinc-100 tracking-tight tabular-nums`}>
                      {creditRemainingText}
                    </span>
                    {!creditShowLabels && (
                      <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                        {t("dashboard.creditsRemaining")}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1.5">
                    {t("dashboard.creditTotal")}{" "}
                    <b className="font-semibold text-zinc-700 dark:text-zinc-300">{fmtNum(creditTotal)}</b>
                    <span className="mx-1.5 text-zinc-300 dark:text-zinc-600">•</span>
                    <b className="font-semibold text-zinc-700 dark:text-zinc-300">{fmtNum(creditUsed)}</b>{" "}
                    {t("dashboard.creditUsed")}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(buyCreditPath)}
                className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-white text-[13px] font-semibold rounded-lg transition-colors flex-shrink-0 shadow-sm shadow-primary/30"
              >
                {t("dashboard.rechargeCredit")}
              </button>
            </div>
            {/* Progress footer */}
            <div className="h-2.5 rounded-full bg-primary-100/80 dark:bg-zinc-700 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${creditPct <= 20 ? "bg-red-500" : "bg-gradient-to-r from-primary to-orange-400"}`}
                style={{ width: `${creditPct}%` }}
              />
            </div>
          </div>
          )}

          {/* Stat cards — fill the left column, card bg matches icon color.
              8 slots (7 stats + 1 reserved empty) → a clean 2×4 grid on desktop. */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3.5">
            {bottomStats.map(({ value, label, iconImg, Icon: StatIcon, grad, empty }, i) =>
              empty ? (
                // Reserved placeholder — keeps the grid balanced, holds space for a future stat.
                <div
                  key={i}
                  className="rounded-2xl border border-dashed border-zinc-200 dark:border-zinc-700"
                  aria-hidden="true"
                />
              ) : (
                <div
                  key={i}
                  className={`rounded-2xl p-4 flex items-center gap-3 bg-gradient-to-br ${grad} shadow-md`}
                >
                  <span className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    {StatIcon ? (
                      <StatIcon className="w-6 h-6 text-white" stroke={1.8} />
                    ) : (
                      <img
                        src={iconImg}
                        alt=""
                        className="w-6 h-6 object-contain brightness-0 invert"
                        draggable="false"
                      />
                    )}
                  </span>
                  <div className="min-w-0">
                    <p className="text-[22px] font-bold leading-none tracking-tight text-white">
                      {fmtNum(value)}
                    </p>
                    <p className="text-[11px] font-medium text-white/80 mt-1 truncate">
                      {label}
                    </p>
                  </div>
                </div>
              )
            )}
          </div>
        </div>

      </div>

      <style>{`
                @keyframes dashboard-skeleton-shimmer {
                    0%   { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
            `}</style>

      {/* ── Modals ── */}
      <ImagePreviewModal
        opened={showImagePreviewModal}
        onClose={() => setShowImagePreviewModal(false)}
        images={stats.recentImages}
        initialImage={selectedPreviewImage}
        onImageDeleted={handleImageDeleted}
        onPrint={(imageUrl, imageId) => {
          setPrintImageUrl(imageUrl);
          setPrintImageId(imageId);
          setShowPrintModal(true);
        }}
        onCustomDownload={(imageUrl, imageId) => {
          setCustomDownloadImageUrl(imageUrl);
          setCustomDownloadImageId(imageId);
          setShowCustomDownload(true);
        }}
      />

      <CustomDownloadModal
        opened={showCustomDownload}
        onClose={() => setShowCustomDownload(false)}
        imageUrl={customDownloadImageUrl}
        imageId={customDownloadImageId}
      />

      <PrintModal
        opened={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        imageUrl={printImageUrl}
        imageId={printImageId}
      />

      {/* Poster editors (full-screen windows) opened from the quick-link tiles. */}
      <BiggaponModal open={posterModal === "jobPoster"} onClose={() => setPosterModal(null)} />
      <RateChartModal open={posterModal === "rateChart"} onClose={() => setPosterModal(null)} />
    </div>
  );
}
